import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, X, RotateCcw, Send } from 'lucide-react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const API_ENDPOINT = '/api/chat';
const STORAGE_KEY = 'pacific78-chatbot-messages-v1';
const AUTOOPEN_KEY = 'pacific78-chatbot-autoopen';
const MAX_MESSAGE_CHARS = 600;

function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredMessages(messages: ChatMessage[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // ignore storage failures (private browsing, quota, etc.)
  }
}

const Chatbot: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadStoredMessages());
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const suggestions = [
    t('chatbot.suggestion1'),
    t('chatbot.suggestion2'),
    t('chatbot.suggestion3'),
    t('chatbot.suggestion4')
  ];

  useEffect(() => {
    saveStoredMessages(messages);
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen, isSending]);

  useEffect(() => {
    let alreadyOpened = false;
    try {
      alreadyOpened = sessionStorage.getItem(AUTOOPEN_KEY) === '1';
    } catch {
      // ignore
    }

    if (!alreadyOpened) {
      const timer = setTimeout(() => {
        try {
          sessionStorage.setItem(AUTOOPEN_KEY, '1');
        } catch {
          // ignore
        }
        setIsOpen(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim().slice(0, MAX_MESSAGE_CHARS);
    if (!trimmed || isSending) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setNotice(null);
    setIsSending(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          language: i18n.language,
          history: nextMessages.slice(-6)
        })
      });

      const data = await response.json().catch(() => null);

      if (response.status === 429) {
        setNotice(t('chatbot.errorRateLimited'));
        return;
      }

      if (response.status === 503) {
        setNotice(t('chatbot.unavailable'));
        return;
      }

      if (!response.ok || !data?.ok) {
        setNotice(t('chatbot.errorGeneric'));
        return;
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: data.answer }]);
    } catch {
      setNotice(t('chatbot.errorGeneric'));
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendMessage(input);
  };

  const handleReset = () => {
    setMessages([]);
    setNotice(null);
    saveStoredMessages([]);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      <AnimatePresence>
        {isOpen && (
          <motion.section
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-modal="false"
            aria-label={t('chatbot.title')}
            className="mb-4 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white border border-luxury-gold/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-4 bg-luxury-black">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-luxury-gold/15 border border-luxury-gold/50 flex items-center justify-center text-luxury-gold">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <h2 className="font-display text-lg text-luxury-champagne leading-tight">
                    {t('chatbot.title')}
                  </h2>
                  <p className="font-body text-sm text-luxury-gold tracking-wide">
                    {t('chatbot.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleReset}
                  aria-label={t('chatbot.reset')}
                  className="p-2 text-luxury-champagne/70 hover:text-luxury-gold transition-colors duration-200"
                >
                  <RotateCcw size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label={t('chatbot.close')}
                  className="p-2 text-luxury-champagne/70 hover:text-luxury-gold transition-colors duration-200"
                >
                  <X size={20} />
                </button>
              </div>
            </header>

            <div
              role="log"
              aria-live="polite"
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-luxury-cream"
            >
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-luxury-champagne/40 border border-luxury-gold/20 text-luxury-black shadow-sm px-4 py-3 font-body text-base">
                  {t('chatbot.welcome')}
                </div>
              </div>

              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 font-body text-base whitespace-pre-wrap ${
                      message.role === 'user'
                        ? 'bg-luxury-black text-luxury-gold rounded-2xl rounded-br-sm shadow-sm'
                        : 'bg-luxury-champagne/40 border border-luxury-gold/20 text-luxury-black shadow-sm rounded-2xl rounded-bl-sm'
                    }`}
                  >
                    {message.content}
                  </div>
                </div>
              ))}

              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-luxury-champagne/40 border border-luxury-gold/20 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold animate-bounce [animation-delay:-0.2s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold animate-bounce [animation-delay:-0.1s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold animate-bounce" />
                  </div>
                </div>
              )}

              {notice && (
                <p className="text-center text-sm text-luxury-gold font-body italic">{notice}</p>
              )}

              <div ref={messagesEndRef} />
            </div>

            {messages.length === 0 && (
              <div className="px-4 pb-3 pt-1 flex flex-wrap gap-2 bg-luxury-cream">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => sendMessage(suggestion)}
                    className="text-sm font-body px-3 py-1.5 rounded-full border border-luxury-gold/40 bg-luxury-champagne/40 text-luxury-black hover:border-luxury-gold hover:text-luxury-gold transition-colors duration-200"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="flex items-end gap-2 px-4 py-3 border-t border-luxury-gold/15 bg-white"
            >
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage(input);
                  }
                }}
                rows={1}
                maxLength={MAX_MESSAGE_CHARS}
                placeholder={t('chatbot.placeholder') as string}
                className="flex-1 resize-none bg-luxury-cream text-luxury-black placeholder:text-luxury-gray-dark/60 font-body text-base rounded-lg px-3 py-2 border border-luxury-gold/20 focus:border-luxury-gold focus:outline-none max-h-24"
              />
              <button
                type="submit"
                disabled={isSending || !input.trim()}
                aria-label={t('chatbot.send') as string}
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-luxury-gold text-luxury-black disabled:opacity-40 disabled:cursor-not-allowed hover:bg-luxury-black hover:text-luxury-gold transition-colors duration-200"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? (t('chatbot.close') as string) : (t('chatbot.open') as string)}
        aria-expanded={isOpen}
        className="w-14 h-14 rounded-full bg-luxury-gold text-luxury-black shadow-2xl flex items-center justify-center border border-luxury-black/10 hover:bg-luxury-black hover:text-luxury-gold transition-colors duration-200"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>
    </div>
  );
};

export default Chatbot;
